import { Fragment, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Loader2, MessageSquareMore, Package } from "lucide-react";
import { PedidoCard, fetchSingleOrderItems, OrderItem } from "@/services/suprimentos";
import { formatCurrency, formatDate, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";

/* ─── Status helpers ─────────────────────────────────────────────────────── */
const STATUS_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
  "recebido":    { bg: "bg-emerald-50",  text: "text-emerald-700", dot: "bg-emerald-500" },
  "ag. entrega": { bg: "bg-amber-50",    text: "text-amber-700",   dot: "bg-amber-500"   },
  "pendente":    { bg: "bg-red-50",      text: "text-red-700",     dot: "bg-red-500"     },
};

function statusLabel(p: PedidoCard): string {
  if (p.received)              return "Recebido";
  if (p.hasFinancialGenerated) return "Ag. Entrega";
  return "Pendente";
}

function StatusBadge({ pedido }: { pedido: PedidoCard }) {
  const label  = statusLabel(pedido);
  const colors = STATUS_COLOR[label.toLowerCase()] ?? { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-400" };
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold", colors.bg, colors.text)}>
      <span className={cn("w-1.5 h-1.5 rounded-full", colors.dot)} />
      {label}
    </span>
  );
}

/* ─── Sub-tabela de insumos ─────────────────────────────────────────────── */
function ItemsRow({ orderId }: { orderId: string }) {
  const [items,   setItems]   = useState<OrderItem[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchSingleOrderItems(orderId)
      .then((res) => setItems(res))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <tr>
        <td colSpan={99} className="px-6 py-3 bg-slate-50/80">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Carregando insumos…
          </div>
        </td>
      </tr>
    );
  }

  if (!items?.length) {
    return (
      <tr>
        <td colSpan={99} className="px-6 py-2.5 bg-slate-50/60 text-xs text-slate-400 italic">
          Nenhum insumo encontrado para este pedido.
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={99} className="p-0 border-b border-slate-100">
        <div className="bg-slate-50/50 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left px-6 py-2 font-semibold text-slate-400 uppercase tracking-wide">Cód.</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-400 uppercase tracking-wide">Descrição</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-400 uppercase tracking-wide">Qtd.</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-400 uppercase tracking-wide">Un.</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-400 uppercase tracking-wide">Preço Unit.</th>
                <th className="text-right px-4 py-2 font-semibold text-slate-400 uppercase tracking-wide">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it, i) => (
                <tr key={`${it.itemNumber}-${i}`} className="hover:bg-white transition-colors">
                  <td className="px-6 py-2 text-slate-500 font-mono">{it.code || it.itemNumber || `${i + 1}`}</td>
                  <td className="px-3 py-2 text-slate-700 max-w-[200px]">
                    <span className="block truncate" title={it.description}>{it.description || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-center text-slate-600">{it.quantity > 0 ? formatNumber(it.quantity, 2) : "—"}</td>
                  <td className="px-3 py-2 text-center text-slate-400">{it.unit || "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">
                    {(it.netPrice > 0 || it.unitPrice > 0) ? formatCurrency(it.netPrice > 0 ? it.netPrice : it.unitPrice) : "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-slate-700">{it.totalAmount > 0 ? formatCurrency(it.totalAmount) : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-100/60">
                <td colSpan={5} className="px-6 py-1.5 text-xs font-semibold text-slate-400 uppercase">
                  {items.length} insumo{items.length !== 1 ? "s" : ""}
                </td>
                <td className="px-4 py-1.5 text-right text-xs font-bold text-slate-700">
                  {formatCurrency(items.reduce((s, it) => s + (it.totalAmount ?? 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </td>
    </tr>
  );
}

/* ─── Paginação ──────────────────────────────────────────────────────────── */
const PAGE_SIZES = [10, 20, 50];

function Pagination({
  total, page, pageSize, onPage, onPageSize,
}: {
  total: number; page: number; pageSize: number;
  onPage: (p: number) => void; onPageSize: (s: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1
  );

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 rounded-b-xl flex-wrap gap-2">
      <p className="text-xs text-slate-500">
        Mostrando <span className="font-semibold text-slate-700">{from}</span> a <span className="font-semibold text-slate-700">{to}</span> de <span className="font-semibold text-slate-700">{total}</span> pedido{total !== 1 ? "s" : ""}
      </p>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(page - 1)} disabled={page <= 1}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {pages.map((p, idx) => {
          const prev = pages[idx - 1];
          return (
            <Fragment key={p}>
              {prev && p - prev > 1 && (
                <span className="px-1 text-slate-400 text-xs">…</span>
              )}
              <button
                onClick={() => onPage(p)}
                className={cn(
                  "min-w-[28px] h-7 px-2 rounded-lg text-xs font-medium transition-colors",
                  p === page
                    ? "bg-brand-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-200"
                )}
              >
                {p}
              </button>
            </Fragment>
          );
        })}

        <button
          onClick={() => onPage(page + 1)} disabled={page >= totalPages}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <select
        value={pageSize}
        onChange={(e) => { onPageSize(Number(e.target.value)); onPage(1); }}
        className="text-xs text-slate-600 border border-slate-200 rounded-lg px-2 py-1 bg-white hover:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400 transition-colors"
      >
        {PAGE_SIZES.map((s) => (
          <option key={s} value={s}>{s} por página</option>
        ))}
      </select>
    </div>
  );
}

/* ─── Tabela principal ───────────────────────────────────────────────────── */
interface Props {
  pedidos:      PedidoCard[];
  hideCols?:    ("centro" | "fornecedor")[];
  expandable?:  boolean;
  defaultPageSize?: number;
}

export function PedidosTable({
  pedidos,
  hideCols    = [],
  expandable  = false,
  defaultPageSize = 10,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(defaultPageSize);

  // Reset página quando a lista mudar
  useEffect(() => { setPage(1); }, [pedidos]);

  const showCentro     = !hideCols.includes("centro");
  const showFornecedor = !hideCols.includes("fornecedor");
  const toggle = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  const sliced = pedidos.slice((page - 1) * pageSize, page * pageSize);

  if (pedidos.length === 0) {
    return (
      <div className="rounded-xl border border-slate-100 overflow-hidden">
        <div className="py-12 flex flex-col items-center gap-2 text-slate-400">
          <Package className="w-8 h-8 opacity-40" />
          <p className="text-sm font-medium">Nenhum pedido encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {expandable && <th className="w-8" />}
              <th className="text-left px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide whitespace-nowrap">Pedido</th>
              <th className="text-left px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide whitespace-nowrap flex items-center gap-1">
                Data
              </th>
              {showFornecedor && <th className="text-left px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide">Fornecedor</th>}
              {showCentro    && <th className="text-left px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide">Centro de Custo</th>}
              <th className="text-center px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide">Itens</th>
              <th className="text-right px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide whitespace-nowrap">Valor</th>
              <th className="text-center px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide">Status</th>
              <th className="text-center px-3 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wide whitespace-nowrap">Obs.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sliced.map((p, i) => {
              const id         = p.purchaseOrderId ?? String((page - 1) * pageSize + i);
              const isExpanded = expandedId === id;
              return (
                <Fragment key={id}>
                  <tr
                    className={cn(
                      "transition-colors",
                      expandable ? "cursor-pointer" : undefined,
                      isExpanded ? "bg-brand-50/40" : "hover:bg-slate-50/80"
                    )}
                    onClick={expandable ? () => toggle(id) : undefined}
                  >
                    {expandable && (
                      <td className="pl-3 py-3 text-slate-400">
                        {isExpanded
                          ? <ChevronUp   className="w-3.5 h-3.5" />
                          : <ChevronDown className="w-3.5 h-3.5" />}
                      </td>
                    )}
                    <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                      #{p.orderNumber ?? p.purchaseOrderId}
                    </td>
                    <td className="px-3 py-3 text-slate-500 whitespace-nowrap text-xs">
                      {p.date ? formatDate(p.date) : "—"}
                    </td>
                    {showFornecedor && (
                      <td className="px-3 py-3 text-slate-700 max-w-[160px]">
                        <span className="block truncate text-sm" title={p.supplier}>{p.supplier || "—"}</span>
                      </td>
                    )}
                    {showCentro && (
                      <td className="px-3 py-3 text-slate-600 max-w-[140px] text-xs">
                        <span className="block truncate" title={p.costCenterName}>{p.costCenterName || "—"}</span>
                      </td>
                    )}
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center justify-center gap-1 text-slate-500">
                        <Package className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs font-medium">
                          {(p.itemCount > 0
                            ? p.itemCount
                            : (p.itemsPreview?.length ?? 0)) || "—"}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-800 whitespace-nowrap">
                      {formatCurrency(p.value ?? 0)}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <StatusBadge pedido={p} />
                    </td>
                    <td className="px-3 py-3 text-center">
                      {Boolean(p.observations?.trim()) ? (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                          title={p.observations}
                          aria-label="Ver observações do pedido"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MessageSquareMore className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                  </tr>

                  {expandable && isExpanded && (
                    <ItemsRow orderId={id} />
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        total={pedidos.length}
        page={page}
        pageSize={pageSize}
        onPage={setPage}
        onPageSize={setPageSize}
      />
    </div>
  );
}
